/**
 * Table filtering utilities and types
 * Provides comprehensive filtering capabilities for table data
 */

// Filter types and interfaces
export type FilterType = 
  | 'text' 
  | 'select' 
  | 'multiselect' 
  | 'range' 
  | 'date' 
  | 'daterange' 
  | 'boolean' 
  | 'number';

export type FilterOperator = 
  | 'equals' 
  | 'not_equals' 
  | 'contains' 
  | 'not_contains' 
  | 'starts_with' 
  | 'ends_with' 
  | 'greater_than' 
  | 'less_than' 
  | 'greater_than_or_equal' 
  | 'less_than_or_equal' 
  | 'between' 
  | 'in' 
  | 'not_in' 
  | 'is_empty' 
  | 'is_not_empty';

export interface FilterValue {
  value: any;
  operator: FilterOperator;
  type: FilterType;
}

export interface ColumnFilter {
  id: string;
  columnKey: string;
  type: FilterType;
  value: FilterValue | null;
  options?: Array<{ label: string; value: any }>; // For select/multiselect
  placeholder?: string;
  min?: number; // For range/number filters
  max?: number; // For range/number filters
}

export interface FilterState {
  filters: Record<string, ColumnFilter>;
  globalSearch: string;
  logic: 'AND' | 'OR';
}

// Default filter operators for each type
export const DEFAULT_OPERATORS: Record<FilterType, FilterOperator[]> = {
  text: ['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'],
  select: ['equals', 'not_equals', 'in', 'not_in'],
  multiselect: ['in', 'not_in'],
  range: ['between', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
  date: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal'],
  daterange: ['between'],
  boolean: ['equals', 'not_equals'],
  number: ['equals', 'not_equals', 'greater_than', 'less_than', 'greater_than_or_equal', 'less_than_or_equal', 'between']
};

// Operator labels for UI
export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  equals: 'Equals',
  not_equals: 'Not equals',
  contains: 'Contains',
  not_contains: 'Does not contain',
  starts_with: 'Starts with',
  ends_with: 'Ends with',
  greater_than: 'Greater than',
  less_than: 'Less than',
  greater_than_or_equal: 'Greater than or equal',
  less_than_or_equal: 'Less than or equal',
  between: 'Between',
  in: 'In',
  not_in: 'Not in',
  is_empty: 'Is empty',
  is_not_empty: 'Is not empty'
};

/**
 * Create a default filter state
 */
export const createDefaultFilterState = (): FilterState => ({
  filters: {},
  globalSearch: '',
  logic: 'AND'
});

/**
 * Create initial filter state (alias for createDefaultFilterState)
 * Used to initialize empty filter state for tables
 */
export const createInitialFilterState = createDefaultFilterState;

/**
 * Create a column filter configuration
 */
export const createColumnFilter = (
  columnKey: string,
  type: FilterType,
  options?: Array<{ label: string; value: any }>,
  overrides?: Partial<ColumnFilter>
): ColumnFilter => ({
  id: `filter_${columnKey}`,
  columnKey,
  type,
  value: null,
  options,
  placeholder: `Filter ${columnKey}...`,
  ...overrides
});

/**
 * Apply a single filter to a value
 */
export const applyFilter = (value: any, filter: FilterValue): boolean => {
  const { value: filterValue, operator, type } = filter;

  // Handle empty/null values
  if (value === null || value === undefined || value === '') {
    switch (operator) {
      case 'is_empty':
        return true;
      case 'is_not_empty':
        return false;
      default:
        return false;
    }
  }

  // Handle is_empty/is_not_empty operators
  if (operator === 'is_empty') return false;
  if (operator === 'is_not_empty') return true;

  // Convert values for comparison
  const normalizedValue = normalizeValue(value, type);
  const normalizedFilterValue = normalizeValue(filterValue, type);

  switch (operator) {
    case 'equals':
      return normalizedValue === normalizedFilterValue;
    
    case 'not_equals':
      return normalizedValue !== normalizedFilterValue;
    
    case 'contains':
      return String(normalizedValue).toLowerCase().includes(String(normalizedFilterValue).toLowerCase());
    
    case 'not_contains':
      return !String(normalizedValue).toLowerCase().includes(String(normalizedFilterValue).toLowerCase());
    
    case 'starts_with':
      return String(normalizedValue).toLowerCase().startsWith(String(normalizedFilterValue).toLowerCase());
    
    case 'ends_with':
      return String(normalizedValue).toLowerCase().endsWith(String(normalizedFilterValue).toLowerCase());
    
    case 'greater_than':
      return normalizedValue > normalizedFilterValue;
    
    case 'less_than':
      return normalizedValue < normalizedFilterValue;
    
    case 'greater_than_or_equal':
      return normalizedValue >= normalizedFilterValue;
    
    case 'less_than_or_equal':
      return normalizedValue <= normalizedFilterValue;
    
    case 'between':
      if (Array.isArray(normalizedFilterValue) && normalizedFilterValue.length === 2) {
        return normalizedValue >= normalizedFilterValue[0] && normalizedValue <= normalizedFilterValue[1];
      }
      return false;
    
    case 'in':
      if (Array.isArray(normalizedFilterValue)) {
        return normalizedFilterValue.includes(normalizedValue);
      }
      return normalizedValue === normalizedFilterValue;
    
    case 'not_in':
      if (Array.isArray(normalizedFilterValue)) {
        return !normalizedFilterValue.includes(normalizedValue);
      }
      return normalizedValue !== normalizedFilterValue;
    
    default:
      return true;
  }
};

/**
 * Normalize value based on filter type
 */
const normalizeValue = (value: any, type: FilterType): any => {
  switch (type) {
    case 'number':
      return Number(value);
    
    case 'date':
    case 'daterange':
      return new Date(value);
    
    case 'boolean':
      if (typeof value === 'boolean') return value;
      if (typeof value === 'string') {
        return value.toLowerCase() === 'true' || value === '1' || value === 'yes';
      }
      return Boolean(value);
    
    case 'text':
    case 'select':
    case 'multiselect':
    default:
      return value;
  }
};

/**
 * Apply filters to a dataset
 */
export const applyFilters = <T extends Record<string, any>>(
  data: T[],
  filterState: FilterState
): T[] => {
  let filteredData = [...data];

  // Apply global search
  if (filterState.globalSearch.trim()) {
    const searchTerm = filterState.globalSearch.toLowerCase();
    filteredData = filteredData.filter(row =>
      Object.values(row).some(value => 
        String(value || '').toLowerCase().includes(searchTerm)
      )
    );
  }

  // Apply column filters
  const activeFilters = Object.values(filterState.filters).filter(filter => filter.value !== null);
  
  if (activeFilters.length === 0) {
    return filteredData;
  }

  return filteredData.filter(row => {
    const results = activeFilters.map(filter => {
      const columnValue = row[filter.columnKey];
      return filter.value ? applyFilter(columnValue, filter.value) : true;
    });

    // Apply logic (AND/OR)
    return filterState.logic === 'AND' 
      ? results.every(Boolean) 
      : results.some(Boolean);
  });
};

/**
 * Get filter summary for display
 */
export const getFilterSummary = (filterState: FilterState): string => {
  const activeFilters = Object.values(filterState.filters).filter(filter => filter.value !== null);
  const globalSearchActive = filterState.globalSearch.trim().length > 0;
  
  if (activeFilters.length === 0 && !globalSearchActive) {
    return 'No filters applied';
  }

  const parts: string[] = [];
  
  if (globalSearchActive) {
    parts.push(`Search: "${filterState.globalSearch}"`);
  }
  
  if (activeFilters.length > 0) {
    parts.push(`${activeFilters.length} column filter${activeFilters.length === 1 ? '' : 's'}`);
  }

  return parts.join(', ');
};

/**
 * Clear all filters
 */
export const clearFilters = (filterState: FilterState): FilterState => ({
  ...filterState,
  filters: Object.fromEntries(
    Object.entries(filterState.filters).map(([key, filter]) => [
      key,
      { ...filter, value: null }
    ])
  ),
  globalSearch: ''
});

/**
 * Clear a specific filter
 */
export const clearFilter = (filterState: FilterState, columnKey: string): FilterState => ({
  ...filterState,
  filters: {
    ...filterState.filters,
    [columnKey]: {
      ...filterState.filters[columnKey],
      value: null
    }
  }
});

/**
 * Set a filter value
 */
export const setFilter = (
  filterState: FilterState,
  columnKey: string,
  value: FilterValue | null
): FilterState => ({
  ...filterState,
  filters: {
    ...filterState.filters,
    [columnKey]: {
      ...filterState.filters[columnKey],
      value
    }
  }
});

/**
 * Set global search
 */
export const setGlobalSearch = (filterState: FilterState, search: string): FilterState => ({
  ...filterState,
  globalSearch: search
});

/**
 * Toggle filter logic (AND/OR)
 */
export const toggleFilterLogic = (filterState: FilterState): FilterState => ({
  ...filterState,
  logic: filterState.logic === 'AND' ? 'OR' : 'AND'
});

/**
 * Get unique values for a column (for select filters)
 */
export const getUniqueColumnValues = <T extends Record<string, any>>(
  data: T[],
  columnKey: string
): Array<{ label: string; value: any }> => {
  const values = new Set(data.map(row => row[columnKey]).filter(val => val !== null && val !== undefined));
  return Array.from(values)
    .sort()
    .map(value => ({
      label: String(value),
      value
    }));
};

/**
 * Serialize filter state to URL-safe string
 */
export const serializeFilterState = (filterState: FilterState): string => {
  try {
    return btoa(JSON.stringify(filterState));
  } catch {
    return '';
  }
};

/**
 * Deserialize filter state from URL-safe string
 */
export const deserializeFilterState = (serialized: string): FilterState | null => {
  try {
    return JSON.parse(atob(serialized));
  } catch {
    return null;
  }
};